import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Archive, ArchiveRestore, ArrowLeft, Building2, Check, Copy, CreditCard, FileText, Gift, Key, Link2, LoaderCircle, Pencil, Plus, RefreshCcw, Trash2, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import ContractStatus from "@/components/contracts/ContractStatus";
import { adminContractApi, adminContractErrorMessage, buildContractDraftPayload, buildCreditPayload, buildDepartmentPatchPayload, buildLegacyMigrationPayload, canGrantCredit, creditReasonLabel, creditStatusLabel, resolveDepartmentContracts } from "@/lib/contractAdmin";
import { formatEuro } from "@/lib/contractApi";
import { cancellationChannelLabel, contractPaymentDue, documentedAccessLabel, formatContractDate, isManuallyBlocked } from "@/lib/contractUser";
import { networkMembershipLabel, networkRequest } from "@/lib/networkManagement";

const initialForm = {
  abteilungId: "",
  name: "",
  email: "",
  tariffCode: "single",
  durationMonths: 12,
  startDate: new Date().toISOString().slice(0, 10),
  overrideEuro: "",
};

export default function WacheDetail() {
  const { wacheId } = useParams();
  const [wache, setWache] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [creditsByContract, setCreditsByContract] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState(initialForm);
  const [newDepartment, setNewDepartment] = useState({ name: "", username: "", password: "" });
  const [departmentEdit, setDepartmentEdit] = useState(null);
  const [copied, setCopied] = useState("");
  const [callbackUrl, setCallbackUrl] = useState("");
  const [legacySetup, setLegacySetup] = useState(null);
  const [lifecycleDialog, setLifecycleDialog] = useState(null);
  const [legacyDueDialog, setLegacyDueDialog] = useState(null);
  const [creditDialog, setCreditDialog] = useState(null);
  const [creditVoidDialog, setCreditVoidDialog] = useState(null);
  const [successorLink, setSuccessorLink] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [networkMembership, setNetworkMembership] = useState(null);

  const load = useCallback(async () => {
    try {
      const [wacheResponse, departmentResponse, contractResponse] = await Promise.all([
        axios.get(`${API}/wachen/${wacheId}`),
        axios.get(`${API}/abteilungen`, { params: { wache_id: wacheId } }),
        adminContractApi.listForWache(wacheId),
      ]);
      setWache(wacheResponse.data);
      setCallbackUrl(wacheResponse.data.callback_url || "");
      setDepartments(departmentResponse.data || []);
      setContracts(contractResponse.data || []);
      const creditContracts = contractResponse.data || [];
      const creditResponses = await Promise.all(creditContracts.map(async (contract) => {
        try {
          const response = await adminContractApi.listCredits(contract.id);
          return [contract.id, response.data || []];
        } catch {
          return [contract.id, []];
        }
      }));
      setCreditsByContract(Object.fromEntries(creditResponses));
      setForm((current) => ({ ...current, abteilungId: current.abteilungId || departmentResponse.data?.[0]?.id || "" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Wache konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [wacheId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!wache?.id) return;
    let active = true;
    (async () => {
      try {
        const networkStatus = await networkRequest("GET", "/status");
        if (!networkStatus.authenticated) return active && setNetworkMembership(null);
        const networkCatalog = await networkRequest("GET", "/catalog");
        const instance = networkCatalog.instances.find((item) => item.payment_wache_id === wache.id);
        const network = networkCatalog.networks.find((item) => item.id === instance?.network_id);
        if (active) setNetworkMembership(instance ? { instance, network } : { instance: null, network: null });
      } catch { if (active) setNetworkMembership(null); }
    })();
    return () => { active = false; };
  }, [wache?.id]);

  const saveTechnicalSettings = async () => {
    setBusy("technical-settings");
    try {
      const normalizedUrl = callbackUrl.trim().replace(/\/+$/, "");
      await axios.patch(`${API}/wachen/${wacheId}`, { callback_url: normalizedUrl });
      toast.success("Technische Einstellungen gespeichert");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Technische Einstellungen konnten nicht gespeichert werden");
    } finally {
      setBusy("");
    }
  };

  const regenerateApiKey = async () => {
    if (!window.confirm("API-Key wirklich erneuern? Der bisherige Schlüssel wird sofort ungültig.")) return;
    setBusy("api-key");
    try {
      await axios.post(`${API}/wachen/${wacheId}/regenerate-api-key`);
      toast.success("API-Key erneuert");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "API-Key konnte nicht erneuert werden");
    } finally {
      setBusy("");
    }
  };

  const copyApiKey = async () => {
    await navigator.clipboard.writeText(wache.api_key);
    setCopied("api-key");
    toast.success("API-Key kopiert");
  };

  const contractsByDepartment = useMemo(() => {
    return Object.fromEntries(
      departments.map((department) => [
        department.id,
        resolveDepartmentContracts(department, contracts),
      ])
    );
  }, [contracts, departments]);

  const createDepartment = async (event) => {
    event.preventDefault();
    setBusy("department");
    try {
      const { data } = await axios.post(`${API}/abteilungen`, {
        wache_id: wacheId,
        name: newDepartment.name,
        betrag: 48,
        intervall: "yearly",
        erstmalige_zahlung: form.startDate,
        user_username: newDepartment.username || null,
        user_password: newDepartment.password || null,
      });
      setForm((current) => ({ ...current, abteilungId: data.id }));
      setNewDepartment({ name: "", username: "", password: "" });
      toast.success("Wachabteilung angelegt. Sie bleibt Legacy, bis ein Vertrag abgeschlossen wird.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Wachabteilung konnte nicht angelegt werden");
    } finally { setBusy(""); }
  };

  const createDraft = async (event) => {
    event.preventDefault();
    setBusy("draft");
    try {
      const payload = buildContractDraftPayload({ ...form, wacheId });
      const { data } = await adminContractApi.createDraft(payload);
      toast.success(`Vertragsentwurf ${data.contract_number} erstellt`);
      setForm((current) => ({ ...initialForm, abteilungId: current.abteilungId }));
      await load();
    } catch (error) {
      toast.error(adminContractErrorMessage(error, "Vertrag konnte nicht vorbereitet werden"));
    } finally { setBusy(""); }
  };

  const beginDepartmentEdit = (department) => {
    setDepartmentEdit({
      id: department.id,
      name: department.name || "",
      username: department.user_username || "",
      password: "",
      status: department.status || "active",
      billingMode: department.billing_mode || "legacy",
      amountEuro: String(department.betrag ?? ""),
      interval: department.intervall || "yearly",
      firstPayment: department.erstmalige_zahlung || "",
    });
  };

  const saveDepartment = async (event) => {
    event.preventDefault();
    if (!departmentEdit) return;
    setBusy(`department:${departmentEdit.id}`);
    try {
      await axios.patch(
        `${API}/abteilungen/${departmentEdit.id}`,
        buildDepartmentPatchPayload(departmentEdit)
      );
      setDepartmentEdit(null);
      toast.success("Wachabteilung aktualisiert");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Wachabteilung konnte nicht aktualisiert werden");
    } finally {
      setBusy("");
    }
  };

  const prepareContract = (departmentId) => {
    setForm((current) => ({ ...current, abteilungId: departmentId }));
    document.getElementById("department-setup")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const prepareLegacyContract = async (department) => {
    setBusy(`legacy-preflight:${department.id}`);
    try {
      const { data } = await adminContractApi.legacyPreflight(wacheId, department.id);
      setLegacySetup({ department, preflight: data, name: "", email: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bestandsdaten konnten nicht geprüft werden");
    } finally {
      setBusy("");
    }
  };

  const createLegacyContract = async (event) => {
    event.preventDefault();
    if (!legacySetup) return;
    setBusy(`legacy-create:${legacySetup.department.id}`);
    try {
      const { data } = await adminContractApi.createLegacyDraft(
        wacheId,
        legacySetup.department.id,
        buildLegacyMigrationPayload(legacySetup)
      );
      toast.success(`Bestandsvertrag ${data.contract_number} vorbereitet`);
      setLegacySetup(null);
      await load();
    } catch (error) {
      toast.error(adminContractErrorMessage(error, "Bestandsvertrag konnte nicht erstellt werden"));
    } finally {
      setBusy("");
    }
  };

  const openContractCopy = async (contract) => {
    try {
      await adminContractApi.openCopy(contract.id);
    } catch (error) {
      toast.error(adminContractErrorMessage(error, "Vertragskopie konnte nicht geöffnet werden"));
    }
  };

  const deleteDepartment = async (department) => {
    if (!window.confirm(`Wachabteilung „${department.name}“ wirklich löschen? Dies ist nur ohne Verträge und Zahlungen möglich.`)) return;
    setBusy(`delete:${department.id}`);
    try {
      await axios.delete(`${API}/abteilungen/${department.id}`);
      toast.success("Unbenutzte Wachabteilung gelöscht");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Wachabteilung konnte nicht gelöscht werden");
    } finally {
      setBusy("");
    }
  };

  const issueInvitation = async (contract) => {
    setBusy(`invite:${contract.id}`);
    try {
      const { data } = await adminContractApi.issueInvitation(contract.id, false);
      await navigator.clipboard.writeText(data.url);
      setCopied(contract.id);
      toast.success("Einladungslink kopiert");
      await load();
    } catch (error) { toast.error(error.response?.data?.detail || "Einladung konnte nicht erstellt werden"); }
    finally { setBusy(""); }
  };

  const discardInvitation = async (contract) => {
    if (!window.confirm("Einladung wirklich verwerfen? Der Link wird sofort ungültig. Ein angenommener Vertrag kann nicht gelöscht werden.")) return;
    setBusy(`discard:${contract.id}`);
    try {
      await adminContractApi.discard(contract.id);
      toast.success("Einladung verworfen");
      await load();
    } catch (error) {
      toast.error(adminContractErrorMessage(error, "Einladung konnte nicht verworfen werden"));
    } finally {
      setBusy("");
    }
  };

  const recordPayment = async (contract) => {
    if (!window.confirm(`Zahlung für ${formatEuro(contract.terms.price_cents)} verbindlich erfassen?`)) return;
    setBusy(`payment:${contract.id}`);
    try {
      await adminContractApi.recordPayment(contract.id, { paid_on: new Date().toISOString().slice(0, 10), payment_method: "bank_transfer" });
      toast.success("Zahlung und neuer Nutzungszeitraum erfasst");
      await load();
    } catch (error) { toast.error(error.response?.data?.detail || "Zahlung konnte nicht erfasst werden"); }
    finally { setBusy(""); }
  };

  const openCreditDialog = (contract) => {
    setCreditDialog({ contract, months: "1", reason: "referral", note: "", preview: null });
  };

  const previewCredit = async () => {
    if (!creditDialog) return;
    try {
      const payload = buildCreditPayload(creditDialog);
      setBusy(`credit-preview:${creditDialog.contract.id}`);
      const { data } = await adminContractApi.previewCredit(creditDialog.contract.id, payload);
      setCreditDialog((current) => ({ ...current, preview: data }));
    } catch (error) {
      toast.error(adminContractErrorMessage(error, error.message || "Vorschau konnte nicht berechnet werden"));
    } finally {
      setBusy("");
    }
  };

  const submitCredit = async (event) => {
    event.preventDefault();
    if (!creditDialog?.preview) return;
    const contractId = creditDialog.contract.id;
    try {
      const payload = buildCreditPayload(creditDialog);
      setBusy(`credit-grant:${contractId}`);
      const idempotencyKey = globalThis.crypto?.randomUUID?.() || `credit-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      await adminContractApi.grantCredit(contractId, payload, idempotencyKey);
      toast.success(`${payload.months} Gratis-Monate eingebucht`);
      setCreditDialog(null);
      await load();
    } catch (error) {
      toast.error(adminContractErrorMessage(error, error.message || "Gratis-Monate konnten nicht eingebucht werden"));
    } finally {
      setBusy("");
    }
  };

  const openCreditVoidDialog = async (contract, credit) => {
    try {
      setBusy(`credit-void-preview:${credit.id}`);
      const { data } = await adminContractApi.previewVoidCredit(contract.id, credit.id);
      setCreditVoidDialog({ contract, credit, preview: data, reason: "" });
    } catch (error) {
      toast.error(adminContractErrorMessage(error, "Storno-Vorschau konnte nicht berechnet werden"));
    } finally {
      setBusy("");
    }
  };

  const submitCreditVoid = async (event) => {
    event.preventDefault();
    if (!creditVoidDialog) return;
    const { contract, credit, reason } = creditVoidDialog;
    try {
      setBusy(`credit-void:${credit.id}`);
      await adminContractApi.voidCredit(contract.id, credit.id, { reason: reason.trim() || null });
      toast.success("Gratis-Monate storniert und Zeitachse neu berechnet");
      setCreditVoidDialog(null);
      await load();
    } catch (error) {
      toast.error(adminContractErrorMessage(error, "Gratis-Monate konnten nicht storniert werden"));
    } finally {
      setBusy("");
    }
  };

  const submitLegacyDueCorrection = async (event) => {
    event.preventDefault();
    if (!legacyDueDialog) return;
    const contractId = legacyDueDialog.contract.id;
    setBusy(`legacy-due:${contractId}`);
    try {
      await adminContractApi.correctLegacyPaymentDue(contractId, {
        next_payment_due: legacyDueDialog.nextPaymentDue,
        note: legacyDueDialog.note.trim() || null,
      });
      toast.success("Zahlungsziel des Bestandsvertrags korrigiert");
      setLegacyDueDialog(null);
      await load();
    } catch (error) {
      toast.error(adminContractErrorMessage(error, "Zahlungsziel konnte nicht korrigiert werden"));
    } finally {
      setBusy("");
    }
  };

  const openLifecycleDialog = (type, contract) => {
    setLifecycleDialog({ type, contract, channel: "admin_portal", note: "" });
  };

  const submitLifecycleAction = async (event) => {
    event.preventDefault();
    if (!lifecycleDialog) return;
    const { type, contract, channel, note } = lifecycleDialog;
    setBusy(`lifecycle:${contract.id}`);
    try {
      if (type === "cancel") await adminContractApi.cancel(contract.id, { channel, note: note.trim() || null });
      if (type === "revoke") await adminContractApi.revokeCancellation(contract.id, { channel, note: note.trim() || null });
      if (type === "archive") await adminContractApi.archive(contract.id, note.trim() || null);
      toast.success(type === "cancel" ? "Kündigung vorgemerkt" : type === "revoke" ? "Kündigung widerrufen" : "Vertrag archiviert");
      setLifecycleDialog(null);
      await load();
    } catch (error) {
      toast.error(adminContractErrorMessage(error, "Vertragsstatus konnte nicht geändert werden"));
    } finally {
      setBusy("");
    }
  };

  const restoreArchivedContract = async (contract) => {
    if (!window.confirm("Vertrag wieder in der normalen Historie anzeigen?")) return;
    try {
      await adminContractApi.restore(contract.id);
      toast.success("Vertrag aus dem Archiv geholt");
      await load();
    } catch (error) {
      toast.error(adminContractErrorMessage(error, "Vertrag konnte nicht wiederhergestellt werden"));
    }
  };

  const reactivate = async (contract) => {
    if (!window.confirm("Vertrag wieder aktivieren?")) return;
    try { await adminContractApi.reactivate(contract.id); toast.success("Vertrag reaktiviert"); await load(); }
    catch (error) { toast.error(error.response?.data?.detail || "Aktion fehlgeschlagen"); }
  };

  const block = async (contract) => {
    const reason = window.prompt("Interner Grund für die dokumentierte Sperre");
    if (!reason?.trim()) return;
    if (!window.confirm("Sperre dokumentieren? In der FW-Kantine wird dadurch nichts automatisch geändert.")) return;
    try { await adminContractApi.block(contract.id, reason.trim()); toast.success("Sperre dokumentiert"); await load(); }
    catch (error) { toast.error(error.response?.data?.detail || "Sperre fehlgeschlagen"); }
  };

  const successor = async (contract) => {
    const name = window.prompt("Vollständiger Name der Nachfolgeperson");
    if (!name) return;
    const email = window.prompt("E-Mail-Adresse der Nachfolgeperson (optional)");
    if (email === null) return;
    try {
      const { data } = await adminContractApi.successor(contract.id, { name, email: email.trim() || null });
      setSuccessorLink({ url: data.url, copied: false, copyError: false });
      toast.success("Eigener Nachfolgevertrag vorbereitet");
      try {
        await navigator.clipboard.writeText(data.url);
        setSuccessorLink((current) => current ? { ...current, copied: true } : current);
      } catch {
        setSuccessorLink((current) => current ? { ...current, copyError: true } : current);
      }
      await load();
    } catch (error) { toast.error(adminContractErrorMessage(error, "Nachfolge konnte nicht vorbereitet werden")); }
  };

  const copySuccessorLink = async () => {
    if (!successorLink?.url) return;
    try {
      await navigator.clipboard.writeText(successorLink.url);
      setSuccessorLink((current) => current ? { ...current, copied: true, copyError: false } : current);
      toast.success("Nachfolger-Link kopiert");
    } catch {
      setSuccessorLink((current) => current ? { ...current, copied: false, copyError: true } : current);
    }
  };

  const proposeTermsChange = async (contract) => {
    const duration = Number(window.prompt("Neue Laufzeit in Monaten (6 oder 12)", String(contract.terms?.duration_months || 12)));
    if (![6, 12].includes(duration)) return toast.error("Bitte 6 oder 12 Monate eingeben");
    const price = window.prompt("Neuer Gesamtpreis in Euro", String((contract.terms?.price_cents || 0) / 100).replace(".", ","));
    if (!price) return;
    const priceCents = Math.round(Number(price.replace(",", ".")) * 100);
    if (!Number.isInteger(priceCents) || priceCents <= 0) return toast.error("Preis ist ungültig");
    const effective = new Date(`${contract.paid_until}T12:00:00`);
    effective.setDate(effective.getDate() + 1);
    try {
      const { data } = await adminContractApi.proposeTermsChange(contract.id, {
        tariff_code: contract.terms?.tariff_code || "single",
        duration_months: duration,
        start_date: effective.toISOString().slice(0, 10),
        price_override_cents: priceCents,
      });
      await navigator.clipboard.writeText(data.url);
      toast.success("Änderungslink erstellt und kopiert");
      await load();
    } catch (error) { toast.error(error.response?.data?.detail || "Änderung konnte nicht vorbereitet werden"); }
  };

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center gap-2 text-slate-600"><LoaderCircle className="h-5 w-5 animate-spin" /> Wache wird geladen …</div>;
  if (!wache) return <div>Wache nicht gefunden.</div>;

  return (
    <div className="mx-auto max-w-7xl space-y-7 animate-fade-in">
      <header>
        <Link to="/wachen" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Zurück zu Wachen</Link>
        <div className="mt-4 flex flex-wrap items-start justify-between gap-4"><div><p className="text-sm font-semibold uppercase tracking-wider text-red-700">Wachenverwaltung</p><h1 className="mt-1 text-4xl font-bold text-slate-950">{wache.name}</h1><p className="mt-2 text-slate-500">{wache.subdomain} · {wache.id}</p></div><ContractStatus status={wache.status === "active" ? "active" : "terminated"} /></div>
      </header>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Info icon={Building2} title="Stammdaten"><p>{wache.name}</p><p className="text-sm text-slate-500">Subdomain: {wache.subdomain}</p></Info>
        <Info icon={Link2} title="Technische Anbindung"><p>{wache.callback_url ? "Callback eingerichtet" : "Nur Pull-Abruf eingerichtet"}</p><p className="mt-2 text-xs text-slate-500">Details und Endpunkte werden weiter unten in dieser Wache verwaltet.</p></Info>
        {networkMembership && <Info icon={Link2} title="Netzwerk"><p>{networkMembershipLabel(networkMembership.instance, networkMembership.network)}</p><Link to="/netzwerke" className="mt-2 inline-block text-xs font-semibold text-blue-700">Netzwerke verwalten</Link></Info>}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" aria-labelledby="technical-settings-title">
        <div className="flex items-start gap-3">
          <span className="rounded-xl bg-slate-100 p-2 text-slate-700"><Key className="h-5 w-5" /></span>
          <div><h2 id="technical-settings-title" className="text-xl font-semibold text-slate-950">Technische Einstellungen</h2><p className="mt-1 text-sm text-slate-600">Verbindung ausschließlich für Feedback, globale Hinweise, Hilfe-Inhalte und Wartungsmodus.</p></div>
        </div>
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-slate-800">API-Key</label>
            <div className="mt-2 flex gap-2"><code className="min-w-0 flex-1 overflow-x-auto rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 font-mono text-xs text-slate-700">{wache.api_key}</code><Action icon={Copy} onClick={copyApiKey}>{copied === "api-key" ? "Kopiert" : "Kopieren"}</Action><Action icon={RefreshCcw} onClick={regenerateApiKey}>{busy === "api-key" ? "…" : "Erneuern"}</Action></div>
            <p className="mt-2 text-xs text-slate-500">Authentifiziert die erlaubten Verbindungen dieser Wache.</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-800">Callback-URL der Kantine</label>
            <div className="mt-2 flex gap-2"><input value={callbackUrl} onChange={(event) => setCallbackUrl(event.target.value)} placeholder="https://fw-kantine.de" className="input-admin min-w-0 flex-1 font-mono text-sm" /><button type="button" onClick={saveTechnicalSettings} disabled={busy === "technical-settings"} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">Speichern</button></div>
            <p className="mt-2 text-xs text-slate-500">Push-Ziele: <code>/announcement</code>, <code>/help-content</code> und <code>/maintenance</code>. Feedback läuft eingehend an die Payment-App.</p>
          </div>
        </div>
      </section>

      <section id="department-setup" className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm scroll-mt-6">
        <div className="border-b border-slate-200 bg-slate-950 px-6 py-5 text-white">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-200">Geführte Einrichtung</p>
          <h2 className="mt-1 text-2xl font-semibold text-white">Wachabteilung &amp; Nutzungsvertrag</h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-100">Neue Abteilung anlegen, anschließend direkt auswählen und den individuellen Vertragsentwurf vorbereiten.</p>
        </div>
        <div className="grid lg:grid-cols-[0.85fr_1.4fr]">
          <div className="border-b border-slate-200 p-6 lg:border-b-0 lg:border-r">
            <SetupHeading number="1" icon={Plus} title="Wachabteilung anlegen" />
            <form onSubmit={createDepartment} className="mt-5 space-y-3">
              <Field label="Name der Wachabteilung"><input required placeholder="z. B. Wachabteilung 1" value={newDepartment.name} onChange={(e) => setNewDepartment({ ...newDepartment, name: e.target.value })} className="input-admin" /></Field>
              <Field label="Benutzername"><input placeholder="optional" value={newDepartment.username} onChange={(e) => setNewDepartment({ ...newDepartment, username: e.target.value })} className="input-admin" /></Field>
              <Field label="Startpasswort"><input type="password" placeholder="optional" value={newDepartment.password} onChange={(e) => setNewDepartment({ ...newDepartment, password: e.target.value })} className="input-admin" /></Field>
              <button disabled={busy === "department"} className="w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:bg-slate-400">Wachabteilung anlegen</button>
            </form>
          </div>
          <div className="p-6">
            <SetupHeading number="2" icon={UserRound} title="Nutzungsvertrag vorbereiten" />
            <p className="mt-2 text-sm text-slate-500">Preis und Zeitraum werden nach dem Speichern serverseitig verbindlich berechnet.</p>
            <form onSubmit={createDraft} className="mt-5 grid gap-4 md:grid-cols-2">
              <Field label="Wachabteilung"><select required value={form.abteilungId} onChange={(e) => setForm({ ...form, abteilungId: e.target.value })} className="input-admin"><option value="">Bitte wählen</option>{departments.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></Field>
              <Field label="Verantwortliche Person"><input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-admin" /></Field>
              <Field label="E-Mail (optional)"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-admin" /></Field>
              <Field label="Nutzungsbeginn"><input required type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className="input-admin" /></Field>
              <Field label="Tarif"><select value={form.tariffCode} onChange={(e) => setForm({ ...form, tariffCode: e.target.value })} className="input-admin"><option value="single">Einzeltarif</option><option value="community">Gemeinschaftstarif</option></select></Field>
              <Field label="Zeitraum"><select value={form.durationMonths} onChange={(e) => setForm({ ...form, durationMonths: Number(e.target.value) })} className="input-admin"><option value={6}>6 Monate</option><option value={12}>12 Monate</option></select></Field>
              <Field label="Individueller Gesamtpreis"><input inputMode="decimal" placeholder="optional, z. B. 53,00" value={form.overrideEuro} onChange={(e) => setForm({ ...form, overrideEuro: e.target.value })} className="input-admin" /></Field>
              <div className="flex items-end"><button disabled={!departments.length || busy === "draft"} className="w-full rounded-xl bg-red-700 px-4 py-3 text-sm font-semibold text-white disabled:bg-slate-300">Vertragsentwurf erstellen</button></div>
            </form>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3"><div><h2 className="text-2xl font-semibold">Wachabteilungen und Verträge</h2><p className="mt-1 text-sm text-slate-500">Legacy-Datensätze bleiben unverändert, bis ein echter Vertrag abgeschlossen wird. Eine Sperrmarkierung dient nur der Dokumentation; es erfolgt keine automatische Sperrung in der FW-Kantine.</p></div>{contracts.some((item) => item.archived_at) && <Action icon={showArchived ? ArchiveRestore : Archive} onClick={() => setShowArchived((value) => !value)}>{showArchived ? "Archiv ausblenden" : "Archiv anzeigen"}</Action>}</div>
        <div className="grid gap-4 xl:grid-cols-2">
          {departments.map((department) => {
            const { current: contract, pending, history = [] } = contractsByDepartment[department.id] || {};
            const visibleHistory = history.filter((item) => showArchived || !item.archived_at);
            return <article key={department.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div><h3 className="text-lg font-semibold">{department.name}</h3><p className="mt-1 text-xs font-mono text-slate-400">{department.id}</p></div>
                <ContractStatus status={contract?.status || "legacy"} />
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                <span>Abteilung: <strong className="text-slate-700">{department.status === "inactive" ? "Inaktiv" : "Aktiv"}</strong></span>
                <span>Dokumentierter Zugang: <strong className="text-slate-700">{documentedAccessLabel(department)}</strong></span>
                <span>Login: <strong className="text-slate-700">{department.user_username || "Nicht vergeben"}</strong></span>
              </div>
              {departmentEdit?.id === department.id ? (
                <form onSubmit={saveDepartment} className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Name"><input required value={departmentEdit.name} onChange={(e) => setDepartmentEdit({ ...departmentEdit, name: e.target.value })} className="input-admin" /></Field>
                    <Field label="Benutzername"><input value={departmentEdit.username} onChange={(e) => setDepartmentEdit({ ...departmentEdit, username: e.target.value })} className="input-admin" /></Field>
                    <Field label="Neues Passwort"><input type="password" placeholder="leer lassen = unverändert" value={departmentEdit.password} onChange={(e) => setDepartmentEdit({ ...departmentEdit, password: e.target.value })} className="input-admin" /></Field>
                    <Field label="Abteilungsstatus"><select value={departmentEdit.status} onChange={(e) => setDepartmentEdit({ ...departmentEdit, status: e.target.value })} className="input-admin"><option value="active">Aktiv</option><option value="inactive">Inaktiv</option></select></Field>
                    {departmentEdit.billingMode !== "contract" && <>
                      <Field label="Legacy-Betrag in Euro"><input required inputMode="decimal" value={departmentEdit.amountEuro} onChange={(e) => setDepartmentEdit({ ...departmentEdit, amountEuro: e.target.value })} className="input-admin" /></Field>
                      <Field label="Legacy-Intervall"><select value={departmentEdit.interval} onChange={(e) => setDepartmentEdit({ ...departmentEdit, interval: e.target.value })} className="input-admin"><option value="monthly">Monatlich</option><option value="quarterly">Vierteljährlich</option><option value="half-yearly">Halbjährlich</option><option value="yearly">Jährlich</option></select></Field>
                      <Field label="Erste Fälligkeit"><input required type="date" value={departmentEdit.firstPayment} onChange={(e) => setDepartmentEdit({ ...departmentEdit, firstPayment: e.target.value })} className="input-admin" /></Field>
                    </>}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button disabled={busy === `department:${department.id}`} className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white">Änderungen speichern</button>
                    <Action icon={X} onClick={() => setDepartmentEdit(null)}>Abbrechen</Action>
                  </div>
                </form>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Action icon={Pencil} onClick={() => beginDepartmentEdit(department)}>Abteilung bearbeiten</Action>
                  {!contract && !pending && (department.billing_mode === "contract"
                    ? <Action icon={UserRound} onClick={() => prepareContract(department.id)}>Vertrag vorbereiten</Action>
                    : <Action icon={UserRound} onClick={() => prepareLegacyContract(department)}>Bestandsvertrag vorbereiten</Action>)}
                  {!contract && !pending && history.length === 0 && <Action icon={Trash2} onClick={() => deleteDepartment(department)}>Unbenutzte Abteilung löschen</Action>}
                </div>
              )}
              {!contract ? <p className="mt-5 rounded-xl bg-stone-50 p-4 text-sm leading-6 text-stone-600">Bestehende Legacy-Zahlungslogik bleibt aktiv. Wähle die Abteilung oben aus, um bewusst einen Vertrag vorzubereiten.</p> : <>
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm"><Value label="Vertragsnummer" value={contract.contract_number} /><Value label="Person" value={contract.responsible_party?.name} /><Value label="Preis" value={formatEuro(contract.terms?.price_cents)} /><Value label="Bezahlt bis" value={contract.paid_until ? formatContractDate(contract.paid_until) : "Noch offen"} />{contract.contract_kind === "legacy_migration" && <Value label="Zahlungsziel Bestand" value={formatContractDate(contractPaymentDue(contract))} />}</div>
                {contract.cancel_requested_at && <div className="mt-3 grid grid-cols-2 gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm"><Value label="Kündigung eingegangen" value={formatContractDate(contract.cancel_requested_at)} /><Value label="Eingangskanal" value={cancellationChannelLabel(contract.cancel_channel)} /></div>}
                <div className="mt-5 flex flex-wrap gap-2">
                  {contract.status === "draft" || contract.status === "invited" ? <><Action icon={Copy} onClick={() => issueInvitation(contract)}>{copied === contract.id ? "Kopiert" : "Link erstellen"}</Action><Action icon={Trash2} onClick={() => discardInvitation(contract)}>Einladung verwerfen</Action></> : null}
                  {contract.contract_kind === "legacy_migration" && !contract.current_period?.payment_id && ["awaiting_payment", "active", "change_pending"].includes(contract.status) && !contract.archived_at && <Action icon={Pencil} onClick={() => setLegacyDueDialog({ contract, nextPaymentDue: contractPaymentDue(contract) || "", note: "" })}>Zahlungsziel ändern</Action>}
                  {["awaiting_payment", "active", "change_pending"].includes(contract.status) && <Action icon={CreditCard} onClick={() => recordPayment(contract)}>Zahlung erfassen</Action>}
                  {canGrantCredit(contract) && <Action icon={Gift} onClick={() => openCreditDialog(contract)}>Gratis-Monate vergeben</Action>}
                  {contract.status === "active" && contract.paid_until && <Action onClick={() => openLifecycleDialog("cancel", contract)}>Kündigung erfassen</Action>}
                  {contract.status === "cancel_at_period_end" && <Action icon={RefreshCcw} onClick={() => openLifecycleDialog("revoke", contract)}>Kündigung widerrufen</Action>}
                  {contract.status === "active" && <Action icon={UserRound} onClick={() => successor(contract)}>Nachfolge</Action>}
                  {contract.status === "active" && contract.paid_until && <Action onClick={() => proposeTermsChange(contract)}>Preis/Text ändern</Action>}
                  {contract.acceptance_snapshot && <Action icon={FileText} onClick={() => openContractCopy(contract)}>Vertragskopie öffnen</Action>}
                  {!isManuallyBlocked(contract) && !["withdrawn", "terminated"].includes(contract.status) && <Action onClick={() => block(contract)}>Sperre dokumentieren</Action>}
                  {isManuallyBlocked(contract) && <Action icon={RefreshCcw} onClick={() => reactivate(contract)}>Sperrmarkierung aufheben</Action>}
                </div>
                {(creditsByContract[contract.id] || []).length > 0 && <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                  <h4 className="text-sm font-semibold text-emerald-950">Gratis-Monate</h4>
                  <div className="mt-3 space-y-2">
                    {creditsByContract[contract.id].map((credit) => <div key={credit.id} className="rounded-lg border border-emerald-200 bg-white p-3 text-xs">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div><p className="font-semibold text-slate-900">{credit.months} {credit.months === 1 ? "Gratis-Monat" : "Gratis-Monate"} · {creditReasonLabel(credit.reason)}</p><p className="mt-1 text-slate-500">{creditStatusLabel(credit.status)}{credit.note ? ` · ${credit.note}` : ""}</p></div>
                        {credit.status !== "voided" && <Action icon={X} onClick={() => openCreditVoidDialog(contract, credit)}>Stornieren</Action>}
                      </div>
                      {credit.status === "applied" && <p className="mt-2 text-slate-600">Zeitraum {formatContractDate(credit.period_start)} bis {formatContractDate(credit.period_end)} · nächste Zahlung {formatContractDate(credit.new_next_payment_due)}</p>}
                      {credit.status === "pending" && <p className="mt-2 text-slate-600">Wird nach der ersten gültigen Zahlung hinten angehängt.</p>}
                      {credit.status === "voided" && <p className="mt-2 text-slate-500">Storniert{credit.void_reason ? `: ${credit.void_reason}` : ""}</p>}
                    </div>)}
                  </div>
                </div>}
              </>}
              {pending && <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2"><div><p className="text-xs font-semibold uppercase tracking-wider text-amber-800">Vorgemerkter Nachfolgevertrag</p><p className="mt-1 font-mono text-xs text-amber-900">{pending.contract_number}</p></div><ContractStatus status={pending.status} /></div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["draft", "invited"].includes(pending.status) && <><Action icon={Copy} onClick={() => issueInvitation(pending)}>Link neu erstellen</Action><Action icon={Trash2} onClick={() => discardInvitation(pending)}>Einladung verwerfen</Action></>}
                  {pending.status === "awaiting_payment" && <Action icon={CreditCard} onClick={() => recordPayment(pending)}>Zahlung erfassen</Action>}
                  {canGrantCredit(pending) && <Action icon={Gift} onClick={() => openCreditDialog(pending)}>Gratis-Monate vergeben</Action>}
                </div>
              </div>}
              {visibleHistory.length > 0 && <div className="mt-5 border-t border-slate-200 pt-4">
                <h4 className="text-sm font-semibold text-slate-900">Vertragshistorie</h4>
                <div className="mt-3 space-y-2">
                  {visibleHistory.map((historical) => <div key={historical.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div><p className="font-mono text-xs font-semibold text-slate-800">{historical.contract_number}</p><p className="mt-1 text-xs text-slate-500">{historical.responsible_party?.name || "Keine Person"} · {historical.accepted_at ? formatContractDate(historical.accepted_at) : "Noch nicht abgeschlossen"}</p></div>
                      <ContractStatus status={historical.status} />
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                      <Value label="Beginn" value={historical.acceptance_snapshot?.terms?.period_start ? formatContractDate(historical.acceptance_snapshot.terms.period_start) : historical.terms?.period_start ? formatContractDate(historical.terms.period_start) : "Noch offen"} />
                      <Value label="Vertragszeitraum bis" value={historical.acceptance_snapshot?.terms?.period_end ? formatContractDate(historical.acceptance_snapshot.terms.period_end) : historical.terms?.period_end ? formatContractDate(historical.terms.period_end) : "Offen"} />
                      <Value label="Preis / Intervall" value={`${formatEuro(historical.acceptance_snapshot?.terms?.price_cents ?? historical.terms?.price_cents)} / ${historical.acceptance_snapshot?.terms?.duration_months ?? historical.terms?.duration_months ?? "–"} Monate`} />
                      <Value label="Bezahlt bis" value={(historical.migration?.paid_until || historical.paid_until) ? formatContractDate(historical.migration?.paid_until || historical.paid_until) : "Noch offen"} />
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {historical.contract_kind === "legacy_migration" && <span className="rounded-full bg-stone-200 px-2 py-1 text-[11px] font-semibold text-stone-700">Bestandsvertrag</span>}
                      {historical.archived_at && <span className="rounded-full bg-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-700">Archiviert</span>}
                      {historical.acceptance_snapshot && <Action icon={FileText} onClick={() => openContractCopy(historical)}>Vertragskopie öffnen</Action>}
                      {historical.id !== pending?.id && ["draft", "invited"].includes(historical.status) && !historical.accepted_at && !historical.acceptance_snapshot && <Action icon={Trash2} onClick={() => discardInvitation(historical)}>Einladung verwerfen</Action>}
                      {["terminated", "withdrawn"].includes(historical.status) && !historical.archived_at && <Action icon={Archive} onClick={() => openLifecycleDialog("archive", historical)}>Archivieren</Action>}
                      {historical.archived_at && <Action icon={ArchiveRestore} onClick={() => restoreArchivedContract(historical)}>Aus Archiv holen</Action>}
                    </div>
                  </div>)}
                </div>
              </div>}
            </article>;
          })}
          {!departments.length && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500 xl:col-span-2">Noch keine Wachabteilung angelegt. Starte oben mit Schritt 1.</div>}
        </div>
      </section>
      {legacySetup && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4" role="dialog" aria-modal="true" aria-labelledby="legacy-setup-title">
        <form onSubmit={createLegacyContract} className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div><p className="text-xs font-semibold uppercase tracking-wider text-red-700">Bestandsmigration</p><h2 id="legacy-setup-title" className="mt-1 text-2xl font-semibold text-slate-950">Bestandsvertrag vorbereiten</h2><p className="mt-2 text-sm text-slate-500">Der Vertrag beginnt mit der Bestätigung sofort. Bestehende Zahlungen bleiben unverändert.</p></div>
            <button type="button" onClick={() => setLegacySetup(null)} aria-label="Schließen" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 rounded-xl bg-stone-50 p-4 text-sm">
            <Value label="Bisheriger Preis" value={formatEuro(legacySetup.preflight.migration.price_cents)} />
            <Value label="Intervall" value={legacyIntervalLabel(legacySetup.preflight.migration.interval)} />
            <Value label="Bezahlt bis" value={legacySetup.preflight.migration.paid_until ? formatContractDate(legacySetup.preflight.migration.paid_until) : "Kein bezahlter Zeitraum"} />
            <Value label="Nächste Zahlung" value={formatContractDate(legacySetup.preflight.migration.next_payment_due)} />
            <Value label="Erhaltene Zahlungen" value={String(legacySetup.preflight.migration.legacy_payment_count)} />
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Verantwortliche Privatperson"><input required minLength={2} value={legacySetup.name} onChange={(event) => setLegacySetup({ ...legacySetup, name: event.target.value })} className="input-admin" /></Field>
            <Field label="E-Mail-Adresse (optional)"><input type="email" value={legacySetup.email} onChange={(event) => setLegacySetup({ ...legacySetup, email: event.target.value })} className="input-admin" /></Field>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={() => setLegacySetup(null)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700">Abbrechen</button>
            <button disabled={busy === `legacy-create:${legacySetup.department.id}`} className="rounded-xl bg-red-700 px-4 py-2.5 text-sm font-semibold text-white disabled:bg-slate-300">Bestandsvertrag erstellen</button>
          </div>
        </form>
      </div>}
      {legacyDueDialog && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4" role="dialog" aria-modal="true" aria-labelledby="legacy-due-title">
        <form onSubmit={submitLegacyDueCorrection} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4"><div><p className="text-xs font-semibold uppercase tracking-wider text-red-700">Bestandsabrechnung</p><h2 id="legacy-due-title" className="mt-1 text-2xl font-semibold text-slate-950">Zahlungsziel korrigieren</h2><p className="mt-2 text-sm text-slate-500">Die Unterzeichnung und die gespeicherte Vertragskopie bleiben unverändert. Die Korrektur wird protokolliert.</p></div><button type="button" onClick={() => setLegacyDueDialog(null)} aria-label="Schließen" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button></div>
          <div className="mt-5 grid gap-4">
            <Field label="Ursprüngliches Zahlungsziel"><input required type="date" value={legacyDueDialog.nextPaymentDue} onChange={(event) => setLegacyDueDialog({ ...legacyDueDialog, nextPaymentDue: event.target.value })} className="input-admin" /></Field>
            <Field label="Interne Notiz (optional)"><textarea maxLength={1000} rows={3} value={legacyDueDialog.note} onChange={(event) => setLegacyDueDialog({ ...legacyDueDialog, note: event.target.value })} className="input-admin resize-y" /></Field>
          </div>
          <div className="mt-6 flex justify-end gap-2"><button type="button" onClick={() => setLegacyDueDialog(null)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700">Abbrechen</button><button disabled={busy === `legacy-due:${legacyDueDialog.contract.id}`} className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">Korrektur speichern</button></div>
        </form>
      </div>}
      {creditDialog && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4" role="dialog" aria-modal="true" aria-labelledby="credit-dialog-title">
        <form onSubmit={submitCredit} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4"><div><p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Vertragsgutschrift</p><h2 id="credit-dialog-title" className="mt-1 text-2xl font-semibold text-slate-950">Gratis-Monate vergeben</h2><p className="mt-2 text-sm text-slate-500">{creditDialog.contract.contract_number} · Der Zeitraum wird immer hinten angehängt.</p></div><button type="button" onClick={() => setCreditDialog(null)} aria-label="Schließen" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button></div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Volle Monate"><input required min="1" step="1" type="number" value={creditDialog.months} onChange={(event) => setCreditDialog({ ...creditDialog, months: event.target.value, preview: null })} className="input-admin" /></Field>
            <Field label="Grund"><select value={creditDialog.reason} onChange={(event) => setCreditDialog({ ...creditDialog, reason: event.target.value, preview: null })} className="input-admin"><option value="referral">Werbeprogramm</option><option value="marketing">Marketing</option><option value="other">Sonstiges</option></select></Field>
            <div className="sm:col-span-2"><Field label="Interne Notiz (optional)"><textarea maxLength={1000} rows={3} value={creditDialog.note} onChange={(event) => setCreditDialog({ ...creditDialog, note: event.target.value, preview: null })} className="input-admin resize-y" /></Field></div>
          </div>
          {creditDialog.preview && <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm">
            {creditDialog.preview.mode === "pending" ? <><p className="font-semibold text-emerald-950">Vorgemerkt bis zur ersten Zahlung</p><p className="mt-1 text-emerald-800">Die Gratis-Monate beginnen erst nach dem ersten bezahlten Vertragszeitraum.</p></> : <><p className="font-semibold text-emerald-950">Neuer Zahlungstermin: {formatContractDate(creditDialog.preview.new_next_payment_due)}</p><p className="mt-1 text-emerald-800">Gratis-Zeitraum: {formatContractDate(creditDialog.preview.period_start)} bis {formatContractDate(creditDialog.preview.period_end)}</p></>}
          </div>}
          <div className="mt-6 flex flex-wrap justify-end gap-2"><button type="button" onClick={() => setCreditDialog(null)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700">Abbrechen</button><button type="button" onClick={previewCredit} disabled={busy === `credit-preview:${creditDialog.contract.id}`} className="rounded-xl border border-emerald-700 px-4 py-2.5 text-sm font-semibold text-emerald-800 disabled:opacity-60">Vorschau berechnen</button><button disabled={!creditDialog.preview || busy === `credit-grant:${creditDialog.contract.id}`} className="rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white disabled:bg-slate-300">Gratis-Monate einbuchen</button></div>
        </form>
      </div>}
      {creditVoidDialog && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4" role="dialog" aria-modal="true" aria-labelledby="credit-void-title">
        <form onSubmit={submitCreditVoid} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4"><div><p className="text-xs font-semibold uppercase tracking-wider text-red-700">Korrektur</p><h2 id="credit-void-title" className="mt-1 text-2xl font-semibold text-slate-950">Gratis-Monate stornieren</h2><p className="mt-2 text-sm text-slate-500">Die Buchung bleibt im Verlauf sichtbar. Alle späteren Zeiträume werden neu berechnet.</p></div><button type="button" onClick={() => setCreditVoidDialog(null)} aria-label="Schließen" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button></div>
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm"><p className="font-semibold text-amber-950">{creditVoidDialog.credit.months} Gratis-Monate werden entfernt</p><p className="mt-1 text-amber-800">Aktueller Zahlungstermin: {formatContractDate(creditVoidDialog.preview.current_next_payment_due)} · danach: {formatContractDate(creditVoidDialog.preview.new_next_payment_due)}</p></div>
          <div className="mt-4"><Field label="Stornogrund (optional)"><textarea maxLength={1000} rows={3} value={creditVoidDialog.reason} onChange={(event) => setCreditVoidDialog({ ...creditVoidDialog, reason: event.target.value })} className="input-admin resize-y" /></Field></div>
          <div className="mt-6 flex justify-end gap-2"><button type="button" onClick={() => setCreditVoidDialog(null)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700">Abbrechen</button><button disabled={busy === `credit-void:${creditVoidDialog.credit.id}`} className="rounded-xl bg-red-700 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">Storno bestätigen</button></div>
        </form>
      </div>}
      {successorLink && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4" role="dialog" aria-modal="true" aria-labelledby="successor-link-title">
        <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4"><div><p className="text-xs font-semibold uppercase tracking-wider text-red-700">Nachfolge vorbereitet</p><h2 id="successor-link-title" className="mt-1 text-2xl font-semibold text-slate-950">Nachfolger-Einladung</h2><p className="mt-2 text-sm text-slate-500">Der Vertrag wurde erfolgreich vorbereitet. Gib diesen Link an die Nachfolgeperson weiter.</p></div><button type="button" onClick={() => setSuccessorLink(null)} aria-label="Schließen" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button></div>
          <div className="mt-5"><Field label="Einladungslink"><input readOnly value={successorLink.url} onFocus={(event) => event.currentTarget.select()} className="input-admin font-mono text-xs" /></Field></div>
          {successorLink.copyError && <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Der Browser hat das automatische Kopieren nicht erlaubt. Du kannst den Link oben manuell kopieren.</p>}
          {successorLink.copied && <p className="mt-3 text-sm font-medium text-emerald-700">Link wurde in die Zwischenablage kopiert.</p>}
          <div className="mt-6 flex justify-end gap-2"><button type="button" onClick={() => setSuccessorLink(null)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700">Schließen</button><button type="button" onClick={copySuccessorLink} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"><Copy className="h-4 w-4" />{successorLink.copied ? "Erneut kopieren" : "Link kopieren"}</button></div>
        </div>
      </div>}
      {lifecycleDialog && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4" role="dialog" aria-modal="true" aria-labelledby="lifecycle-dialog-title">
        <form onSubmit={submitLifecycleAction} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4"><div><p className="text-xs font-semibold uppercase tracking-wider text-red-700">Vertragsverwaltung</p><h2 id="lifecycle-dialog-title" className="mt-1 text-2xl font-semibold text-slate-950">{lifecycleDialog.type === "cancel" ? "Kündigung erfassen" : lifecycleDialog.type === "revoke" ? "Kündigung widerrufen" : "Vertrag archivieren"}</h2><p className="mt-2 text-sm text-slate-500">{lifecycleDialog.contract.contract_number}</p></div><button type="button" onClick={() => setLifecycleDialog(null)} aria-label="Schließen" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button></div>
          {lifecycleDialog.type !== "archive" && <div className="mt-5"><Field label="Eingangskanal"><select value={lifecycleDialog.channel} onChange={(event) => setLifecycleDialog({ ...lifecycleDialog, channel: event.target.value })} className="input-admin"><option value="admin_portal">Direkt durch Admin</option><option value="email">E-Mail</option><option value="whatsapp">WhatsApp</option><option value="phone">Telefon</option><option value="other">Sonstiges</option><option value="customer_portal">Kundenportal</option></select></Field></div>}
          <div className="mt-4"><Field label="Interne Notiz (optional)"><textarea maxLength={1000} rows={4} value={lifecycleDialog.note} onChange={(event) => setLifecycleDialog({ ...lifecycleDialog, note: event.target.value })} className="input-admin resize-y" /></Field></div>
          <div className="mt-6 flex justify-end gap-2"><button type="button" onClick={() => setLifecycleDialog(null)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700">Abbrechen</button><button disabled={busy === `lifecycle:${lifecycleDialog.contract.id}`} className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">Speichern</button></div>
        </form>
      </div>}
    </div>
  );
}

const Info = ({ icon: Icon, title, children }) => <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900"><Icon className="h-4 w-4 text-red-700" />{title}</div><div className="text-sm text-slate-700">{children}</div></div>;
const SetupHeading = ({ number, icon: Icon, title }) => <div className="flex items-center gap-3"><span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-50 text-sm font-bold text-red-700">{number}</span><Icon className="h-5 w-5 text-red-700" /><h3 className="text-lg font-semibold text-slate-950">{title}</h3></div>;
const Field = ({ label, children }) => <label className="text-sm font-medium text-slate-700">{label}{children}</label>;
const Value = ({ label, value }) => <div><div className="text-xs uppercase tracking-wider text-slate-400">{label}</div><div className="mt-1 font-medium text-slate-800">{value || "–"}</div></div>;
const Action = ({ icon: Icon = Check, onClick, children }) => <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Icon className="h-3.5 w-3.5" />{children}</button>;
const legacyIntervalLabel = (interval) => ({ monthly: "Monatlich", quarterly: "Vierteljährlich", "half-yearly": "Halbjährlich", yearly: "Jährlich" }[interval] || interval);
